import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import clsx from 'clsx';
import type {
  DocumentListItem,
  FileTask,
  Project,
  SystemStats,
  WorkItem,
} from '@captain-tasko/shared';
import { apiList, apiSingle, apiUpload } from '../lib/api.js';
import { formatBytes, formatNumber, formatWorkItemKey, iconLabelForMime } from '../lib/format.js';
import { MimeIcon } from '../components/MimeIcon.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useRealtime } from '../lib/realtime.js';
import { toast } from '../components/Toast.js';
import { SortableTh } from '../components/SortableTh.js';
import { naturalKey, useTableSort } from '../lib/sort.js';
import { useSession } from '../hooks/useSession.js';

export function Dashboard({ onNavigate }: { onNavigate: (p: string) => void }) {
  const qc = useQueryClient();
  // Active org slug threaded into every Dashboard query key so React
  // Query treats each org as a distinct cache entry — switching from
  // Vault → Default → BI refetches instead of serving stale data from
  // the previously-active org. Paired with the server-side org
  // scoping in /stats so each request actually returns per-org rows.
  const { session } = useSession();
  const orgSlug = session?.activeOrg?.slug ?? null;

  const stats = useQuery({
    queryKey: ['stats', orgSlug],
    queryFn: () => apiSingle<SystemStats>('/stats'),
    refetchInterval: 15_000,
  });
  const docs = useQuery({
    queryKey: ['documents', { limit: 8, org: orgSlug }],
    queryFn: () => apiList<DocumentListItem>('/documents', { query: { limit: 8 } }),
  });
  const recentItems = useQuery({
    queryKey: ['workitems', { limit: 5, org: orgSlug }],
    queryFn: () => apiList<WorkItem>('/workitems', { query: { limit: 5 } }),
  });
  // 0.1.51 — projects list so RecentWorkTable can color each key by the
  // item's project.color. Cached at the ['projects'] key so any other
  // component that fetched it doesn't re-query.
  const projects = useQuery({
    queryKey: ['projects', orgSlug],
    queryFn: () => apiList<Project>('/projects'),
  });
  // ctsk-066 / dashboard-panel-move — per-nest stats now load in the
  // sidebar (AppShell hover-revealed Nests block), not here. The
  // Dashboard no longer needs its own /stats/nests query.

  useRealtime({
    types: [
      'doc.created', 'doc.updated', 'doc.deleted', 'doc.restored',
      'doc.processing_completed', 'task.completed', 'task.failed',
      'work.created', 'work.updated', 'work.status_changed', 'work.deleted',
    ],
    onEvent: () => {
      qc.invalidateQueries({ queryKey: ['stats'] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['workitems'] });
    },
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <div className="page-subtitle">Overview of your corpus and work pipeline.</div>
        </div>
        <UploadButton onNavigate={onNavigate} />
      </div>

      <SectionHeading>Documents</SectionHeading>
      <div className="kpi-row" style={{ marginBottom: 16 }}>
        <KpiCard label="Documents" value={formatNumber(stats.data?.documents.total)} hint="Active in corpus" onClick={() => onNavigate('/documents')} />
        <KpiCard label="Storage used" value={formatBytes(stats.data?.documents.storageBytes)} hint="Sum of active documents" />
        <KpiCard
          label="Deleted documents"
          value={formatNumber(stats.data?.documents.deleted)}
          hint="Soft-deleted (30-day retention)"
          tone={stats.data && stats.data.documents.deleted > 0 ? 'warning' : undefined}
        />
        <KpiCard
          label="Deleted storage"
          value={formatBytes(stats.data?.documents.deletedStorageBytes)}
          hint="Reclaimable after purge"
          tone={stats.data && stats.data.documents.deletedStorageBytes > 0 ? 'warning' : undefined}
        />
      </div>

      <SectionHeading>Pipeline</SectionHeading>
      <div className="kpi-row" style={{ marginBottom: 16 }}>
        <KpiCard
          label="In-flight tasks"
          value={formatNumber((stats.data?.fileTasks.queued ?? 0) + (stats.data?.fileTasks.started ?? 0))}
          hint="Queued + running"
          onClick={() => onNavigate('/tasks/files')}
        />
        <KpiCard
          label="Failed tasks"
          value={formatNumber(stats.data?.fileTasks.failed)}
          hint="Click to retry"
          tone={stats.data && stats.data.fileTasks.failed > 0 ? 'danger' : undefined}
          onClick={() => onNavigate('/tasks/files')}
        />
        <KpiCard
          label="Completed (24h)"
          value={formatNumber(stats.data?.fileTasks.completedLast24h)}
          hint="Last 24 hours"
        />
        <KpiCard label="Tags" value={formatNumber(stats.data?.tags.total)} onClick={() => onNavigate('/tags')} />
      </div>

      <SectionHeading>Work</SectionHeading>
      <div className="kpi-row" style={{ marginBottom: 12 }}>
        <KpiCard label="Projects" value={formatNumber(stats.data?.projects.active)} hint="Active" onClick={() => onNavigate('/projects')} />
        <KpiCard label="Work items" value={formatNumber(stats.data?.workItems.total)} onClick={() => onNavigate('/workitems')} />
        <KpiCard
          label="Assigned to me"
          value={formatNumber(stats.data?.workItems.assignedToMe)}
          // Deep-link with the corresponding URL filter — WorkItemsList
          // reads ?assignee/?stage/?flag on mount and seeds its filter
          // state. Closes the "click does nothing useful" bug from the
          // first dashboard pass.
          onClick={() => onNavigate('/workitems?assignee=mine')}
        />
        <KpiCard
          label="In progress"
          value={formatNumber(stats.data?.workItems.byStage?.started)}
          hint="Started stage"
          onClick={() => onNavigate('/workitems?stage=started')}
        />
      </div>

      {/* Attention strip — three flags lifted out of the work-items list
          so the dashboard surfaces them as primary signals, not buried in a
          single 'Overdue' KPI. Each routes to /workitems with a flag URL
          param that the list narrows on. */}
      <div className="kpi-row kpi-row--attention" style={{ marginBottom: 24 }}>
        <KpiCard
          label="Past due"
          value={formatNumber(stats.data?.workItems.overdueCount)}
          hint="Open items past dueDate"
          tone={stats.data && stats.data.workItems.overdueCount > 0 ? 'danger' : undefined}
          onClick={() => onNavigate('/workitems?flag=overdue')}
        />
        <KpiCard
          label="Blocking"
          value={formatNumber(stats.data?.workItems.blockingCount)}
          hint="Other items depend on these"
          tone={stats.data && stats.data.workItems.blockingCount > 0 ? 'attention' : undefined}
          onClick={() => onNavigate('/workitems?flag=blocking')}
        />
        <KpiCard
          label="Needs attention"
          value={formatNumber(stats.data?.workItems.needsAttentionCount)}
          hint="Blocked / iterating / waiting"
          tone={stats.data && stats.data.workItems.needsAttentionCount > 0 ? 'warning' : undefined}
          onClick={() => onNavigate('/workitems?flag=attention')}
        />
        <KpiCard
          label="Completed (stage)"
          value={formatNumber(stats.data?.workItems.byStage?.completed)}
          hint="Done + verified"
        />
      </div>

      {/* ctsk-066 — Nest stats moved to the sidebar (AppShell.tsx —
          hover-revealed Nests section). The Dashboard no longer
          renders nest cards inline, which lets Latest documents and
          Recent work items balance to a true 50/50 split below. */}

      {/* Equal halves now that the Nest section has moved out of the
          main content area. Both columns get the same 1fr track. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 24 }}>
        <section>
          <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Latest documents</h2>
          <LatestDocsTable rows={docs.data?.data ?? []} onNavigate={onNavigate} />
        </section>

        <section>
          <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Recent work items</h2>
          <RecentWorkTable
            rows={recentItems.data?.data ?? []}
            projects={projects.data?.data ?? []}
            onNavigate={onNavigate}
          />
        </section>
      </div>
    </>
  );
}

function LatestDocsTable({ rows, onNavigate }: { rows: DocumentListItem[]; onNavigate: (p: string) => void }) {
  const { sorted, sort, setSort } = useTableSort(rows, {
    title: (d: DocumentListItem) => d.title,
    status: (d) => d.status,
    health: (d) => d.health,
    updatedAt: (d) => d.updatedAt,
  }, { key: 'updatedAt', dir: 'desc' });

  if (rows.length === 0) return <EmptyState title="Nothing here yet." body="Drop a file or click Upload." />;

  return (
    <table className="grid">
      <thead>
        <tr>
          <th></th>
          <SortableTh sortKey="title" sort={sort} onSortChange={setSort}>Title</SortableTh>
          <SortableTh sortKey="status" sort={sort} onSortChange={setSort}>Status</SortableTh>
          <SortableTh sortKey="health" sort={sort} onSortChange={setSort}>Health</SortableTh>
          <SortableTh sortKey="updatedAt" sort={sort} onSortChange={setSort}>Updated</SortableTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((d) => (
          <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => onNavigate(`/documents/${d.id}`)}>
            <td
              title={iconLabelForMime(d.mimeType)}
              aria-label={iconLabelForMime(d.mimeType)}
              style={{ color: 'var(--text-muted)', width: 28, textAlign: 'center' }}
            >
              <MimeIcon mime={d.mimeType} size={16} />
            </td>
            {/* ctsk-060 — title is no longer bolded; ctsk-065 narrows the
                column + truncates so long titles don't blow out width. */}
            <td
              title={d.title}
              style={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {d.title}
            </td>
            <td><StatusBadge status={d.status} variant="document" /></td>
            <td><StatusBadge status={d.health} variant="health" /></td>
            <td>{new Date(d.updatedAt).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecentWorkTable({
  rows,
  projects,
  onNavigate,
}: {
  rows: WorkItem[];
  /** Project list for per-key color lookup (0.1.51). */
  projects: Project[];
  onNavigate: (p: string) => void;
}) {
  const { sorted, sort, setSort } = useTableSort(rows, {
    // naturalKey() zero-pads numeric suffix so CTSK-2 sorts before CTSK-10.
    key: (w: WorkItem) => naturalKey(w.key),
    title: (w) => w.title,
    status: (w) => w.status,
    updatedAt: (w) => w.updatedAt,
  }, { key: 'updatedAt', dir: 'desc' });

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No work items"
        body="Create a project, then add work."
        action={<button className="btn btn--create" onClick={() => onNavigate('/projects')}>+ Project</button>}
      />
    );
  }

  return (
    <table className="grid">
      <thead>
        <tr>
          <SortableTh sortKey="key" sort={sort} onSortChange={setSort}>Key</SortableTh>
          <SortableTh sortKey="title" sort={sort} onSortChange={setSort}>Title</SortableTh>
          <SortableTh sortKey="status" sort={sort} onSortChange={setSort}>Status</SortableTh>
        </tr>
      </thead>
      <tbody>
        {sorted.map((w) => {
          const projectColor = projects.find((p) => p.id === w.projectId)?.color;
          return (
            <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => onNavigate(`/workitems/${w.id}`)}>
              <td title={formatWorkItemKey(w).secondary ?? undefined}>
                <code
                  // 0.1.51 — key color derived from project.color (matches
                  // WorkItemsList / WorkItemBoard / Watching / WorkItemDetail).
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    color: projectColor ?? 'var(--accent-primary, #2F6BFF)',
                    fontWeight: projectColor ? 600 : undefined,
                  }}
                >
                  {formatWorkItemKey(w).primary}
                </code>
              </td>
              <td>{w.title}</td>
              <td><StatusBadge status={w.status} variant="document" /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function KpiCard({
  label,
  value,
  hint,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Color the value + left-edge accent. 'attention' is the blocking
   *  flag's amber that shows up on the work-items list rows too. */
  tone?: 'danger' | 'warning' | 'attention';
  onClick?: () => void;
}) {
  const toneColor =
    tone === 'danger' ? 'var(--danger-primary)' :
    tone === 'warning' ? 'var(--warning-primary)' :
    tone === 'attention' ? '#B86E4D' :
    undefined;
  return (
    <div
      className={clsx('kpi', onClick && 'kpi--clickable', tone && 'kpi--toned')}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        boxShadow: toneColor ? `inset 3px 0 0 ${toneColor}` : undefined,
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
    >
      <div className="kpi__label">{label}</div>
      <div className="kpi__value" style={{ color: toneColor }}>{value}</div>
      {hint && <div className="kpi__hint">{hint}</div>}
    </div>
  );
}

function UploadButton({ onNavigate }: { onNavigate: (p: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="btn btn--create">
      <input
        type="file"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            const res = await apiUpload<{ document: DocumentListItem; fileTask: FileTask }>('/documents/upload', file);
            toast.success(`Uploaded ${res.document.title}`);
            onNavigate(`/documents/${res.document.id}`);
          } catch (err: unknown) {
            toast.error(err instanceof Error ? err.message : 'Upload failed');
          } finally {
            setBusy(false);
            e.target.value = '';
          }
        }}
      />
      {busy ? 'Uploading…' : '+ Upload document'}
    </label>
  );
}

// ctsk-064 — top-level dashboard section header (Documents / Pipeline /
// Work). Label on the left, thin rule filling the remaining row width
// to the right — the classic "DOCUMENTS ──────────────" pattern.
// Originally was a border-bottom under the full label width, which
// over-emphasized short section names and didn't visually tie the
// label to its row of KPI cards. The flex+rule treatment reads more
// like a magazine subhead.
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 0,
        marginBottom: 8,
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 14,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {children}
      </h2>
      <span
        aria-hidden
        style={{
          flex: 1,
          height: 1,
          background: 'var(--border-subtle)',
        }}
      />
    </div>
  );
}

// ctsk-066 / dashboard-panel-move — NestStatsCard + NestStatBox used
// to live here. They were moved (and renamed) into AppShell.tsx as
// SidebarNestCard / SidebarStat so the hover-revealed sidebar Nests
// block could reuse them. The Dashboard no longer renders nest
// stats inline.
